import React, { useState, useMemo, useEffect, useRef, useContext } from "react";
import {
    GoogleMap,
    Marker,
    InfoWindow,
    DirectionsService,
    DirectionsRenderer,
} from "@react-google-maps/api";
import { useGoogleMaps } from "components/GoogleMapsProvider/GoogleMapsProvider";
import "./gpsTracker.css"
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { UserContext } from "context";

const GpsTracker = ({ vehicleList, activeMarker, setActiveMarker }) => {
    const { masterData } = useContext(UserContext);
    const isLoaded = useGoogleMaps();
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const clustererRef = useRef(null);

    const [directionsMap, setDirectionsMap] = useState({});
    const [requestedDirections, setRequestedDirections] = useState({});

    useEffect(() => {
        setDirectionsMap({});
        setRequestedDirections({});
    }, [activeMarker]);

    // Default to first valid vehicle with GPS
    const firstVehicleWithGPS = vehicleList.find(
        (v) => v?.coordinates?.lat && v?.coordinates?.lng
    );

    const defaultLat = firstVehicleWithGPS
        ? parseFloat(firstVehicleWithGPS.coordinates?.lat)
        : 20.5937;
    const defaultLng = firstVehicleWithGPS
        ? parseFloat(firstVehicleWithGPS.coordinates?.lng)
        : 78.9629;

    const center = useMemo(() => ({ lat: defaultLat, lng: defaultLng }), [defaultLat, defaultLng]);

    const containerStyle = {
        position: 'absolute',
        width: "100%",
        height: "100%",
    };

    useEffect(() => {
        if (!mapRef.current || !window.google || !vehicleList?.length) return;

        // Clear previous markers
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];

        // Destroy previous cluster
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
        }

        // Create new markers
        const newMarkers = vehicleList.map(vehicle => {
            const gps = vehicle;
            if (!gps?.coordinates?.lat || !gps?.coordinates?.lng) return null;

            const statusColor = "red";

            const marker = new window.google.maps.Marker({
                position: {
                    lat: parseFloat(gps?.coordinates?.lat),
                    lng: parseFloat(gps?.coordinates?.lng),
                },
                icon: {
                    url: "https://ik.imagekit.io/fx/markers/vehicle/truck-running-vehicle_marker_base.png",
                    scaledSize: new window.google.maps.Size(30, 60),
                },
                label: {
                    text: getVName(vehicle.vehicle_no),
                    className: "map-label",
                },
            });

            marker.addListener("click", () => {
                setActiveMarker({
                    id: vehicle.id,
                    position: marker.getPosition(),
                    gps,
                    vehicle,
                });
            });

            return marker;
        }).filter(Boolean); // Remove nulls

        markersRef.current = newMarkers;

        // Create clusterer
        clustererRef.current = new MarkerClusterer({
            map: mapRef.current,
            markers: newMarkers,
        });

    }, [vehicleList]);

    const getVName = (v_no) => {
        const match = v_no.match(/[A-Za-z]*\d+$/);
        const result = match ? match[0].replace(/[A-Za-z]/g, '') : '';
        return result;
    };

    const DetailBox = ({ vehicle, gps }) => {
        return (
            <div style={{ minWidth: 300, maxWidth: 300 }}>
                <h6>
                    {/* {getVName(vehicle.vehicle_no)} {" "} */}
                    {vehicle.vehicle_no}
                </h6>
                <p>Driver: {vehicle?.fleet_trip?.fleet_handler?.name ? vehicle?.fleet_trip?.fleet_handler?.name : '-'}</p>
                <p>Last Updated: {vehicle?.dttime}</p>
                <hr />
                <div className="d-flex justify-content-between align-items-center" style={{ maxWidth: '200px' }}>
                    <div>
                        <p style={{ fontSize: '14px', fontWeight:500 }}>Status</p>
                        <p style={{ color: '#000' }}>{vehicle?.status_text ? vehicle?.status_text : '-'} </p>
                    </div>
                    {/* <div>
                        <p style={{ fontSize: '10px' }}>From</p>
                        <p style={{ color: '#000' }}>{vehicle?.summary?.latest_timeline_record?.startLocation}</p>
                    </div> */}
                </div>
                {/* <div className="d-flex justify-content-between align-items-center" style={{ maxWidth: '200px' }}>
                    <div>
                        <p style={{ fontSize: '10px' }}>Status</p>
                        <p style={{ color: '#000' }}>{vehicle?.fleet_trip?.trip_status ? masterData.trip_status[vehicle?.fleet_trip?.trip_status] : '-'} </p>
                    </div>
                    <div>
                        <p style={{ fontSize: '14px', fontWeight:500 }}>From</p>
                        <p style={{ color: '#000' }}>{vehicle?.summary?.latest_timeline_record?.startLocation}</p>
                    </div>
                </div> */}
                <hr />
                <div className="d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                    {/* <div style={{ maxWidth: '20%' }}>
                        <p style={{ fontSize: '10px' }}>Location</p>
                    </div>
                    <div style={{ maxWidth: '80%' }}>
                        {vehicle?.location}
                    </div> */}
                    <div>
                        <p style={{ fontSize: '14px', fontWeight:500 }}>Location</p>
                        <p style={{ color: '#000' }}>{vehicle?.location}</p>
                    </div>
                </div>

            </div>
        )
    }

    function calculateBearing(startLat, startLng, endLat, endLng) {
        const toRadians = (deg) => (deg * Math.PI) / 180;
        const toDegrees = (rad) => (rad * 180) / Math.PI;

        const startLatRad = toRadians(startLat);
        const startLngRad = toRadians(startLng);
        const endLatRad = toRadians(endLat);
        const endLngRad = toRadians(endLng);

        const dLng = endLngRad - startLngRad;
        const y = Math.sin(dLng) * Math.cos(endLatRad);
        const x =
            Math.cos(startLatRad) * Math.sin(endLatRad) -
            Math.sin(startLatRad) * Math.cos(endLatRad) * Math.cos(dLng);

        let brng = Math.atan2(y, x);
        brng = toDegrees(brng);
        return (brng + 360) % 360; // Normalize to 0-360 degrees
    }

    if (!isLoaded) return <div>Loading Map...</div>;

    return (
        <React.Fragment>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={5}
                onLoad={(map) => {
                    mapRef.current = map;
                }}

                options={{
                    mapTypeControl: true,       // ❌ disables the street/map type icon
                    fullscreenControl: true,     // optional: keep fullscreen control
                    streetViewControl: false,    // optional: hide Pegman (street view)
                    zoomControl: false,           // optional: keep zoom controls

                }}
            >
                {vehicleList.map((vehicle) => {
                    const gps = vehicle;
                    if (!gps?.coordinates?.lat || !gps?.coordinates?.lng) return null;

                    const lat = parseFloat(gps?.coordinates?.lat);
                    const lng = parseFloat(gps?.coordinates?.lng);

                    const prevLat = vehicle.start_latitude;   // Or store previous lat long elsewhere
                    const prevLng = vehicle.start_longitude;
                    const bearing = calculateBearing(prevLat, prevLng, lat, lng);

                    return (
                        <React.Fragment>
                            {/* <div class="truck-icon">
                                <div class="status-box"></div>
                                <div class="container-box"></div>
                                <div class="truck-head"></div>
                            </div> */}
                            {/* <Marker
                                key={vehicle._id}
                                position={{ lat, lng }}
                                onClick={() => setActiveMarker(vehicle._id)}
                                // icon={{
                                //     url: "https://img.icons8.com/?size=192&id=afVt8guq75IH&format=png",
                                //     scaledSize: new window.google.maps.Size(32, 32),
                                // }}

                                // icon={{
                                //     path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                //     scale: 5,
                                //     fillColor: "#0f52ff",
                                //     fillOpacity: 1,
                                //     strokeWeight: 1,
                                //     rotation: bearing,  // calculated bearing
                                // }}


                                label={{
                                    text: getVName(vehicle.vehicle_no),
                                    className: "map-label",
                                }}
                            > */}
                            {activeMarker?.vehicle?.vehicle_no === vehicle.vehicle_no && (
                                <InfoWindow
                                    position={activeMarker.position}
                                    onCloseClick={() => setActiveMarker(null)}>
                                    <DetailBox vehicle={activeMarker.vehicle} gps={activeMarker.gps} />
                                </InfoWindow>
                            )}
                            {/* </Marker> */}
                            {activeMarker?.vehicle?.vehicle_no === vehicle.vehicle_no &&
                                vehicle?.start_latitude &&
                                vehicle?.start_longitude &&
                                !directionsMap[vehicle.id] &&
                                !requestedDirections[vehicle.id] && (
                                    <DirectionsService
                                        options={{
                                            origin: {
                                                lat: vehicle.start_latitude,
                                                lng: vehicle.start_longitude,
                                            },
                                            destination: {
                                                lat: parseFloat(vehicle?.coordinates?.lat),
                                                lng: parseFloat(vehicle?.coordinates?.lng),
                                            },
                                            travelMode: window.google.maps.TravelMode.DRIVING,
                                        }}
                                        callback={(res, status) => {
                                            if (status === "OK") {
                                                setDirectionsMap((prev) => ({
                                                    ...prev,
                                                    [vehicle.id]: res,
                                                }));
                                            } else {
                                                console.error("Directions request failed", status);
                                            }

                                            // Mark as requested to avoid re-requesting
                                            setRequestedDirections((prev) => ({
                                                ...prev,
                                                [vehicle.id]: true,
                                            }));
                                        }}
                                    />
                                )}

                            {directionsMap[vehicle.id] && (
                                <DirectionsRenderer
                                    options={{
                                        directions: directionsMap[vehicle.id],
                                        suppressMarkers: true,
                                        polylineOptions: {
                                            strokeColor: "#0f52ff",
                                            strokeOpacity: 0.8,
                                            strokeWeight: 8,
                                        },
                                    }}
                                />
                            )}

                        </React.Fragment>
                    );

                })}

            </GoogleMap>
        </React.Fragment>
    );

};

export default GpsTracker;
